import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { EMPTY_ITEMS, EQueryFetchPolicy, SHOW_FETCH_MORE_LOADER } from 'constants/common';
import {
  CardSchema,
  CardTypeOptions,
  CardListObjectType,
  CardFilter,
  ECardSortingOptions,
  ImageSetSchema,
  useGetAllPicksCoverQuery,
  useGetUserCardsIdQuery,
  useGetUserCardsQuery,
  useGetProfileListsQuery,
  useGetUserRecentCardQuery,
  useGetFilteredUserPicksQuery,
} from 'constants/graphqlTypes';
import { useGetUserId } from 'graphQL/profile/hooks';
import { useGetSpotlights } from 'graphQL/spotlights/hook';
import { getTitlesForCover } from 'components/Collection/helpers';

import {
  IUseAllPicksCover,
  IUseAllPicksCoverArgs,
  IUseGetProfileListsArgs,
  IUseGetProfileLists,
  IUseGetUserCards,
} from './models';
import { updateUserCards } from './helpers';
import { GET_USER_PICKED_TOPICS } from './gql';

interface IUseGetUserCardsParams {
  type?: CardTypeOptions;
  limit?: number;
  excludedIds?: string[];
  sorting?: ECardSortingOptions;
  includeQuestionResponse?: boolean;
  customUserId?: string;
  customSkip?: boolean;
  withImage?: boolean;
  username?: string;
}

export const useGetUserCards = (params?: IUseGetUserCardsParams): IUseGetUserCards => {
  const [wasCompleted, setWasCompleted] = useState(false);
  const { type, limit, excludedIds, sorting, includeQuestionResponse, customSkip, withImage, username } = params ?? {};
  const { profileName } = useParams<{ profileName: string }>();

  // Use provided username or fall back to profileName
  const queryUsername = username || profileName || '';

  const { data, fetchMore, loading } = useGetUserCardsQuery({
    variables: {
      username: queryUsername,
      offset: 0,
      limit,
      type,
      excludedIds,
      sorting,
      includeQuestionResponse: !!includeQuestionResponse,
      withImage,
    },
    onCompleted: () => setWasCompleted(true),
    fetchPolicy: EQueryFetchPolicy.CacheAndNetwork,
    ...SHOW_FETCH_MORE_LOADER,
    skip: !queryUsername || customSkip,
  });
  const { items, totalCount } = data?.allUsers?.items[0]?.pickedCards ?? EMPTY_ITEMS;

  const loadMore = () =>
    items.length < totalCount &&
    fetchMore({
      variables: { offset: items.length },
      updateQuery: (prev, { fetchMoreResult }) => updateUserCards(prev, fetchMoreResult),
    });

  return {
    // Cast to CardSchema[] - the query returns a subset of fields
    recentCards: items as CardSchema[],
    loadMore,
    totalCount,
    loading,
    wasCompleted,
  };
};

export const useGetUserRecentPick = (): { recentPick?: CardSchema; hasSpotlights: boolean } => {
  const { profileName } = useParams<{ profileName: string }>();
  const { userId } = useGetUserId(profileName);
  const { hasSpotlights, cards } = useGetSpotlights();

  const { data } = useGetUserRecentCardQuery({
    variables: {
      userId: userId ?? '',
      ...(hasSpotlights && { excludedIds: cards.map((card) => card.cardId) }),
    },
    fetchPolicy: EQueryFetchPolicy.NetworkOnly,
    skip: !userId,
  });

  return { recentPick: data?.allUsers?.items?.[0]?.pickedCards?.items?.[0], hasSpotlights };
};

export const useGetProfileLists = ({
  limit,
  privacy,
  contentTypeIn = [CardTypeOptions.Collection, CardTypeOptions.Drafts],
  withPrivacy,
  skip,
  customUserId,
}: IUseGetProfileListsArgs): IUseGetProfileLists => {
  const [isQueryCalled, setIsQueryCalled] = useState(false);
  const { profileName } = useParams<{ profileName: string }>();
  const { userId } = useGetUserId(profileName);
  const filters: CardFilter = {
    and: [
      { typeIn: contentTypeIn },
      ...(withPrivacy !== undefined ? [{ withPrivacy }] : []),
      ...(privacy !== undefined ? [{ privacy }] : []),
    ],
  };

  const { data, fetchMore, loading } = useGetProfileListsQuery({
    variables: {
      id: customUserId ?? userId ?? '',
      offset: 0,
      limit,
      filters,
    },
    onCompleted: () => setIsQueryCalled(true),
    fetchPolicy: EQueryFetchPolicy.CacheAndNetwork,
    ...SHOW_FETCH_MORE_LOADER,
    skip: skip ?? (!userId && !customUserId),
  });

  const { items, totalCount } = data?.allUsers?.items[0]?.pickedCards ?? EMPTY_ITEMS;

  const loadMore = () =>
    items.length < totalCount &&
    fetchMore({
      variables: { offset: items.length },
      updateQuery: (prev, { fetchMoreResult }) => updateUserCards(prev, fetchMoreResult),
    });

  return { lists: items, loadMore, totalCount, loading, isQueryCalled };
};

export const useGetUserCardsId = (): { ids: string[]; loadMore: () => void } => {
  const { profileName: username } = useParams<{ profileName: string }>();
  const { userId } = useGetUserId(username);
  const { data, fetchMore, loading } = useGetUserCardsIdQuery({
    variables: { id: userId ?? '' },
    ...SHOW_FETCH_MORE_LOADER,
    fetchPolicy: EQueryFetchPolicy.CacheAndNetwork,
    skip: !userId,
  });

  const { items, totalCount }: CardListObjectType = data?.allUsers?.items[0].pickedCards ?? EMPTY_ITEMS;

  const loadMore = () =>
    items.length < totalCount &&
    !loading &&
    fetchMore({
      variables: { offset: items.length },
      updateQuery: (prev, { fetchMoreResult }) => updateUserCards(prev, fetchMoreResult),
    });

  return { ids: items.map(({ id }) => id), loadMore };
};

export const useAllPicksCover = (args?: IUseAllPicksCoverArgs): IUseAllPicksCover => {
  const { customUserId, skipQuery } = args ?? {};
  const { profileName: username } = useParams<{ profileName?: string }>();
  const { userId } = useGetUserId(username ?? '');

  const { data } = useGetAllPicksCoverQuery({
    variables: { id: customUserId ?? userId },
    skip: (!userId && !customUserId) || skipQuery,
    fetchPolicy: EQueryFetchPolicy.CacheAndNetwork,
  });

  const images =
    data?.allUsers?.items[0].allPicksCover?.items?.reduce(
      (acc: ImageSetSchema[], item) => (item?.__typename === 'ImageSetSchema' && item ? acc.concat(item) : acc),
      []
    ) ?? [];

  const titles = getTitlesForCover(data?.allUsers?.items[0].allPicksCover?.items || []);

  return { images, titles };
};

export const useGetUserPickedTopics = (
  userId?: string,
  cardsSource?: Array<{
    topicTags?: { items?: Array<{ id: number; name: string }> };
    cardMediaTags?: { items?: Array<{ id: number; name: string }> };
  }>
): {
  topics: Array<{ id: number; name: string }>;
  mediaTags: Array<{ id: number; name: string }>;
  loading: boolean;
  error: Error | undefined;
} => {
  const { profileName } = useParams<{ profileName: string }>();
  const { userId: profileUserId } = useGetUserId(profileName);
  const targetUserId = userId ?? profileUserId;

  const { data, loading, error } = useQuery(GET_USER_PICKED_TOPICS, {
    variables: {
      userId: targetUserId ?? '',
      limit: 100,
    },
    fetchPolicy: EQueryFetchPolicy.NetworkOnly,
    skip: !targetUserId,
  });

  // Extract and count occurrences of topics and media tags
  // Prefer caller-provided cardsSource (e.g., saved picks) if available; otherwise use user picked cards
  const pickedCards =
    cardsSource && cardsSource.length > 0 ? cardsSource : data?.allUsers?.items?.[0]?.pickedCards?.items ?? [];

  const topicCounts = new Map<number, { id: number; name: string; count: number }>();
  const mediaTagCounts = new Map<number, { id: number; name: string; count: number }>();

  pickedCards.forEach(
    (card: {
      topicTags?: { items?: Array<{ id: number; name: string }> };
      cardMediaTags?: { items?: Array<{ id: number; name: string }> };
    }) => {
      card.topicTags?.items?.forEach((tag: { id: number; name: string }) => {
        if (tag?.id && tag?.name) {
          const existing = topicCounts.get(tag.id);
          if (existing) {
            existing.count += 1;
          } else {
            topicCounts.set(tag.id, { id: tag.id, name: tag.name, count: 1 });
          }
        }
      });

      card.cardMediaTags?.items?.forEach((mediaTag: { id: number; name: string }) => {
        if (mediaTag?.id && mediaTag?.name) {
          const existing = mediaTagCounts.get(mediaTag.id);
          if (existing) {
            existing.count += 1;
          } else {
            mediaTagCounts.set(mediaTag.id, { id: mediaTag.id, name: mediaTag.name, count: 1 });
          }
        }
      });
    }
  );

  // Sort by count in descending order and return without count property
  const topics = Array.from(topicCounts.values())
    .sort((a, b) => b.count - a.count)
    .map(({ count, ...topic }) => topic);

  const mediaTags = Array.from(mediaTagCounts.values())
    .sort((a, b) => b.count - a.count)
    .map(({ count, ...mediaTag }) => mediaTag);

  return {
    topics,
    mediaTags,
    loading,
    error,
  };
};

export const useGetFilteredUserPicks = (
  filters: {
    topicIds?: number[];
    mediaTagIds?: number[];
    limit?: number;
    sorting?: ECardSortingOptions;
  } = {}
): {
  filteredPicks: CardSchema[];
  loadMore: () => void;
  totalCount: number;
  loading: boolean;
  wasCompleted: boolean;
} => {
  const { profileName } = useParams<{ profileName: string }>();
  const { userId } = useGetUserId(profileName);
  const { topicIds, mediaTagIds, limit, sorting } = filters;

  // Only make the query if there are topic filters or media tag filters
  const hasFilters = (topicIds && topicIds.length > 0) || (mediaTagIds && mediaTagIds.length > 0);

  const { data, fetchMore, loading } = useGetFilteredUserPicksQuery({
    variables: {
      userId: userId ?? '',
      offset: 0,
      limit,
      topicIds,
      mediaTagIds,
      sorting,
    },
    fetchPolicy: EQueryFetchPolicy.CacheAndNetwork,
    skip: !userId || !hasFilters,
  });

  const { items, totalCount } = data?.allUsers?.items?.[0]?.pickedCards ?? EMPTY_ITEMS;

  const loadMore = () =>
    items.length < totalCount &&
    fetchMore({
      variables: {
        offset: items.length,
        topicIds,
        mediaTagIds,
        sorting,
      },
      updateQuery: (prev, { fetchMoreResult }) => updateUserCards(prev, fetchMoreResult),
    });

  return {
    filteredPicks: items,
    loadMore,
    totalCount,
    loading,
    wasCompleted: false, // This state is not managed by the new useGetFilteredUserPicksQuery
  };
};

export function useUserPickedTopics(
  userId: string,
  limit = 100
): {
  loading: boolean;
  error: Error | undefined;
  topics: Array<{ id: string; name: string }>;
  mediaTags: Array<{ id: string; name: string }>;
} {
  const { data, loading, error } = useQuery(GET_USER_PICKED_TOPICS, {
    variables: { userId, limit },
    fetchPolicy: 'cache-and-network',
  });

  // Count occurrences of topics and media tags from all picked cards
  const topicCounts = new Map<string, { id: string; name: string; count: number }>();
  const mediaTagCounts = new Map<string, { id: string; name: string; count: number }>();

  if (data?.allUsers?.items?.[0]?.pickedCards?.items) {
    data.allUsers.items[0].pickedCards.items.forEach(
      (card: {
        tags?: { items?: Array<{ id: string; name: string }> };
        mediaTags?: { items?: Array<{ id: string; name: string }> };
      }) => {
        card.tags?.items?.forEach((tag: { id: string; name: string }) => {
          const existing = topicCounts.get(tag.id);
          if (existing) {
            existing.count += 1;
          } else {
            topicCounts.set(tag.id, { id: tag.id, name: tag.name, count: 1 });
          }
        });
        card.mediaTags?.items?.forEach((tag: { id: string; name: string }) => {
          const existing = mediaTagCounts.get(tag.id);
          if (existing) {
            existing.count += 1;
          } else {
            mediaTagCounts.set(tag.id, { id: tag.id, name: tag.name, count: 1 });
          }
        });
      }
    );
  }

  // Sort by count in descending order and return without count property
  const topics = Array.from(topicCounts.values())
    .sort((a, b) => b.count - a.count)
    .map(({ count, ...topic }) => topic);

  const mediaTags = Array.from(mediaTagCounts.values())
    .sort((a, b) => b.count - a.count)
    .map(({ count, ...mediaTag }) => mediaTag);

  return {
    loading,
    error,
    topics,
    mediaTags,
  };
}
